import { BEGINNING_OF_TIME, DateHelper, INDEFINITE_END_DATE } from 'utils/date';
import { ReactElement } from 'react';
import { RangeStatus } from 'types/enums';
import type { Coworker } from 'types/coworker';
import { GreyedOutCell } from './GreyedOutCell';

type ContractRange = {
    contractStartDate: string;
    contractEndDate: string;
    costCentre: string;
}[];

type IndexRange = {
    startIndex: number;
    endIndex: number;
    costCentre: string;
}[];

/**
 * Returns true when a week index is inside at least one normalized contract range.
 * Range boundaries are interpreted as:
 * - startIndex: inclusive
 * - endIndex: exclusive
 * @param index - Week index in the visible time array.
 * @param normalizedRanges - Contract ranges already normalized to visible week indexes.
 * @returns True if the index is covered by any contract range.
 * @example const ranges = [ { startIndex: 2, endIndex: 5 }, // covers 2, 3, 4   { startIndex: 7, endIndex: 9 }, // covers 7, 8 ];
 * isIndexCoveredByAnyContract(2, ranges); // true
 * isIndexCoveredByAnyContract(5, ranges); // false (end is exclusive)
 * isIndexCoveredByAnyContract(6, ranges); // false
 * isIndexCoveredByAnyContract(8, ranges); // true
 */
const isIndexCoveredByAnyContract = (index: number, gridRowCostCentre: string, normalizedRanges: IndexRange) => normalizedRanges
    .some(({ startIndex, endIndex, costCentre: cc }) => index >= startIndex && index < endIndex && cc === gridRowCostCentre);

export const getGreyedOutCell = (
    costCentre: string,
    contractStartDate: string,
    contractEndDate: string,
    timeArray: Array<string>,
    currentIndex: number,
    key: string,
    futureData?: Array<Partial<Coworker>>,
): // When result is -1 do nothing
ReactElement | RangeStatus => {
    // Initial contract range is the current contract
    const contractRanges: ContractRange = [{
        contractStartDate: contractStartDate || BEGINNING_OF_TIME,
        contractEndDate: contractEndDate || INDEFINITE_END_DATE,
        costCentre
    }];
    const sortFutureContracts = futureData?.filter(item => item.contractStartDate || item.contractEndDate)
        .sort((a, b) => new Date(a.contractStartDate ?? a.contractEndDate ?? INDEFINITE_END_DATE).getTime()
            - new Date(b.contractStartDate ?? b.contractEndDate ?? INDEFINITE_END_DATE).getTime());
    // Add future contract ranges to the list of contract ranges
    sortFutureContracts?.forEach(item => {
        contractRanges.push({
            contractStartDate: item.contractStartDate ?? contractRanges.at(-1)!.contractStartDate,
            contractEndDate: item.contractEndDate ?? contractRanges.at(-1)!.contractEndDate,
            costCentre: item.costCentre ?? contractRanges.at(-1)!.costCentre,
        });
    });

    const ranges = contractRanges.map(({ contractStartDate: startDate, contractEndDate: endDate, costCentre: cc }) => {
        const startWeekIndex = DateHelper.getDateDifferenceInWeeks(startDate, timeArray[0]);
        const endWeekIndex = DateHelper.getDateDifferenceInWeeks(endDate, timeArray[0]);
        const normalizedStartIndex = Math.max(0, startWeekIndex);
        const normalizedEndExclusive = Math.min(timeArray.length, endWeekIndex + 1);

        return {
            startIndex: normalizedStartIndex,
            endIndex: normalizedEndExclusive,
            costCentre: cc
        };
    }).filter(({ startIndex, endIndex }) => endIndex > startIndex);

    // Old logic used -1 as a sentinel for "outside timeframe".
    // In the current logic, if the current index is covered by any normalized contract range,
    // that week is valid (not greyed out), so we return OUTSIDE_RANGE.
    if (isIndexCoveredByAnyContract(currentIndex, costCentre, ranges)) {
        return RangeStatus.OUTSIDE_RANGE;
    }

    // Render the greyed-out td only at the first week of an uncovered gap.
    // If currentIndex is not 0 and previous week is also uncovered, this index is in the middle of
    // the same gap, so we return WITHIN_RANGE to skip rendering a second overlapping GreyedOutCell.
    if (!(currentIndex === 0 || isIndexCoveredByAnyContract(currentIndex - 1, costCentre, ranges))) {
        return RangeStatus.WITHIN_RANGE;
    }

    let gapEndIndex = currentIndex;
    while (gapEndIndex < timeArray.length && !isIndexCoveredByAnyContract(gapEndIndex, costCentre, ranges)) {
        gapEndIndex += 1;
    }

    const greyedOutText = () => {
        if (currentIndex === 0 && gapEndIndex < timeArray.length) {
            return 'CONTRACT_START';
        }

        if (currentIndex > 0 && gapEndIndex === timeArray.length) {
            return 'CONTRACT_END';
        }

        return 'NO_CONTRACT';
    };

    return (
        <GreyedOutCell position={currentIndex === 0 ? 'start' : 'end'} colSpan={gapEndIndex - currentIndex} text={greyedOutText()} key={key} />
    );
};
